import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ErrorsService } from '../../errors/errors-service/errors.service';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';
import { AnalyticsEcommerce, AnalyticsEcommerceEvent, AnalyticsFeatures, AnalyticsUserState } from './analytics-keys';

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void; dataLayer?: unknown[] };

/**
 * DEV ONLY: `ng serve` pages carry no gtag (the tag ships with the server-rendered head), so
 * nothing reaches GA from a dev serve. To watch the events in GA4's DebugView, store the
 * property's measurement id in localStorage under this key (`localStorage.gaDebug = 'G-...'`)
 * and reload: the service loads gtag.js for that id with debug_mode on. Removing the key stops
 * it. The production build never reads it.
 */
const DEBUG_TAG_KEY = 'gaDebug';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private _errorsService = inject(ErrorsService);
    private _browser = inject(BrowserPlatformService);

    constructor() {
        if (!environment.production) {
            this._loadDebugTag();
        }
    }

    public sendEvent(category: string = '', action: string = ''): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        if (!win) {
            return;
        }

        try {
            if (win.gtag) {
                win.gtag('event', category, {
                    operation: action,
                    content_group: this._contentGroup(win),
                });
            }
        } catch (error) {
            if (environment.production) {
                this._errorsService.log(error);
            }
        }
    }

    public page_view(title: string) {
        const win = this._browser.nativeWindow as GtagWindow | null;
        if (win?.gtag) {
            win.gtag('event', 'page_view', {
                page_title: title,
                content_group: this._contentGroup(win),
            });
        }
    }

    /** Updates a user property; it rides every event sent from now on. */
    public setUserState<K extends keyof AnalyticsUserState>(key: K, value: AnalyticsUserState[K]): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        win?.gtag?.('set', 'user_properties', { [key]: value });
    }

    /** Reports an action on a feature (one event per call). */
    public featureUsed<K extends keyof AnalyticsFeatures>(feature: K, variant: AnalyticsFeatures[K]): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        win?.gtag?.('event', 'feature_use', {
            feature,
            variant,
            content_group: win ? this._contentGroup(win) : undefined,
        });
    }

    /**
     * A GA4 ecommerce event (the store's funnel: view_item → begin_checkout → add_shipping_info →
     * add_payment_info → purchase). The payload is sent as-is, GA4's reserved parameter names
     * included; the caller owns the once-per-order guarantee of `purchase`.
     */
    public ecommerce(name: AnalyticsEcommerceEvent, payload: AnalyticsEcommerce): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        if (!win?.gtag) {
            return;
        }
        try {
            win.gtag('event', name, { ...payload, content_group: this._contentGroup(win) });
        } catch (error) {
            if (environment.production) {
                this._errorsService.log(error);
            }
        }
    }


    /** dev only - see DEBUG_TAG_KEY */
    private _loadDebugTag(): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        let id: string | null = null;
        try {
            id = this._browser.localStorage?.getItem(DEBUG_TAG_KEY) ?? null;
        } catch {
            return;
        }
        if (!win || win.gtag || !id || !/^(G|AW)-[A-Z0-9]+$/i.test(id)) {
            return;
        }
        win.dataLayer = win.dataLayer || [];
        //gtag.js reads the Arguments object itself (not an array) - the same shim as the production head
        win.gtag = function () {
            win.dataLayer!.push(arguments);
        };
        win.gtag('js', new Date());
        win.gtag('config', id, { debug_mode: true, content_group: this._contentGroup(win) });
        const script = win.document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
        win.document.head.appendChild(script);
        console.info(`[analytics] dev debug tag ${id} loaded (localStorage.${DEBUG_TAG_KEY})`);
    }

    private _contentGroup(win: Window): string | undefined {
        return win.location.pathname.match(/^\/[^\/]*/)?.[0];
    }
}
